import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type {
  InvitePlatformUserInput,
  UpdatePlatformUserInput,
  UserProfile,
} from "@school-sales-ai/shared";
import { api } from "@/lib/api";
import { useCurrentUserProfile } from "@/hooks/useUsers";

// Platform users (Profile rows with clientId=null) are managed via the
// super-admin API, so — unlike useUsers — there is no client scope. Only
// super-admins can reach these endpoints.
const PLATFORM_USERS_KEY = ["platform-users"] as const;

export function usePlatformUsers() {
  const profile = useCurrentUserProfile();
  const isSuperAdmin = profile?.role === "super_admin";
  return useQuery({
    queryKey: PLATFORM_USERS_KEY,
    queryFn: () => api.get<UserProfile[]>("/super-admin/platform-users"),
    enabled: isSuperAdmin,
  });
}

export function useInvitePlatformUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: InvitePlatformUserInput) =>
      api.post<UserProfile>("/super-admin/platform-users", data),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: PLATFORM_USERS_KEY });
      toast.success(`Invitación enviada a ${variables.email}.`);
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "No se pudo enviar la invitación."),
  });
}

export function useUpdatePlatformUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: UpdatePlatformUserInput & { id: string }) =>
      api.patch<UserProfile>(`/super-admin/platform-users/${id}`, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PLATFORM_USERS_KEY });
      toast.success("Cambios guardados.");
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "No se pudieron guardar los cambios."),
  });
}

export function useDisablePlatformUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string; name: string }) =>
      api.post<null>(`/super-admin/platform-users/${id}/disable`, {}),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: PLATFORM_USERS_KEY });
      toast.success(`${variables.name} fue deshabilitado.`);
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "No se pudo deshabilitar el usuario."),
  });
}

export function useEnablePlatformUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string; name: string }) =>
      api.post<null>(`/super-admin/platform-users/${id}/enable`, {}),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: PLATFORM_USERS_KEY });
      toast.success(`${variables.name} fue habilitado.`);
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "No se pudo habilitar el usuario."),
  });
}

export function useResetPlatformPassword() {
  return useMutation({
    mutationFn: ({ id }: { id: string; email: string }) =>
      api.post<null>(`/super-admin/platform-users/${id}/reset-password`, {}),
    onSuccess: (_data, variables) => {
      toast.success(`Correo de reset enviado a ${variables.email}.`);
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "No se pudo enviar el correo de reset."),
  });
}

export function useResendPlatformInvite() {
  return useMutation({
    mutationFn: ({ id }: { id: string; email: string }) =>
      api.post<null>(`/super-admin/platform-users/${id}/resend-invite`, {}),
    onSuccess: (_data, variables) => {
      toast.success(`Invitación reenviada a ${variables.email}.`);
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "No se pudo reenviar la invitación."),
  });
}
